// The optional photorealistic 3D view (docs/12-solar-roof-scan.md §7.6).
//
// Created only on the first "View in 3D" (Immersive Maps is billed per load) and
// reused afterwards. The camera starts above the building and flies in to look back
// at the main roof face. Panels are Polygon3DElements placed RELATIVE_TO_MESH, lifted
// 0.25 m, near-black and fully opaque with a light frame line:
//   • ABSOLUTE heights from the roof plane height put panels about 0.5 m
//     INSIDE Google's 3D roof (faint ghost outlines), because the mesh sits higher.
//   • Mid-navy or anything see-through washed out under the 3D lighting.
// Each panel element is created once; showing n panels appends the first n and
// removes the rest.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { loadMaps3d } from "./maps";
import type { LL, SolarRoof } from "../types";

export interface View3d {
  /** Show the first n panels. */
  show(n: number): void;
  destroy(): void;
}

/**
 * Build the 3D view inside `container` for this roof and its offered panels. The
 * server sends the camera (roof height and the heading that looks back at the main
 * roof face) and each panel's corners, so nothing is worked out here.
 */
export async function createView3d(
  container: HTMLElement,
  roof: SolarRoof,
  panels: { corners: LL[] }[],
  shown: number,
): Promise<View3d> {
  if (!roof.view3d) throw new Error("No 3D view for this roof");
  const { Map3DElement, Polygon3DElement } = await loadMaps3d();
  const center = { lat: roof.center.lat, lng: roof.center.lng, altitude: roof.view3d.altitude };

  const map3d: any = new Map3DElement({ mode: "SATELLITE", center, range: 140, tilt: 0, heading: 0 });
  map3d.style.width = "100%";
  map3d.style.height = "100%";
  container.replaceChildren(map3d);
  // Fly in to look back at the main roof face
  map3d.flyCameraTo({
    endCamera: { center, range: 45, tilt: 62, heading: roof.view3d.heading },
    durationMillis: 2500,
  });

  const els: any[] = panels.map((p) => {
    const el = new Polygon3DElement({
      altitudeMode: "RELATIVE_TO_MESH",
      fillColor: "#03060cff",
      strokeColor: "#c9d4e6",
      strokeWidth: 1,
      drawsOccludedSegments: false,
    });
    el.path = p.corners.map((c) => ({ lat: c.lat, lng: c.lng, altitude: 0.25 }));
    return el;
  });

  const show = (n: number) => {
    els.forEach((el, i) => {
      if (i < n) {
        if (!el.isConnected) map3d.append(el);
      } else if (el.isConnected) el.remove();
    });
  };
  show(shown);

  return {
    show,
    destroy() {
      els.forEach((el) => el.remove());
      map3d.remove();
    },
  };
}
